/**
 * Highlight, inside a cited chunk, the passages the answer actually drew on.
 *
 * A citation preview shows the whole chunk. When that chunk is a long
 * reference table — a standards appendix, a spreadsheet section — the one
 * sentence that supports the claim is buried in the last column of one row,
 * and the reader has no way to find it short of reading the table. The fix
 * is not to shorten the chunk (the surrounding rows are what make it
 * verifiable) but to point at the part that matched.
 *
 * The signal is the ANSWER text, not the query. The query says what was
 * asked; the answer says what was used. Overlapping word sequences between
 * the answer and the chunk are exactly the spans the model leaned on, so
 * those get marked.
 *
 * Deterministic and local: no model call, no scoring, nothing that can
 * invent a match. A phrase either appears in both texts or it doesn't. The
 * worst failure mode is a missed highlight, never a misleading one.
 */

/** Words shorter than this are still counted inside an n-gram, but an
 * n-gram made ENTIRELY of them is dropped — "of the and to" is not
 * evidence of anything. */
const STOPWORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "but", "by", "for", "from",
  "has", "have", "in", "is", "it", "its", "of", "on", "or", "that", "the",
  "to", "was", "were", "which", "with",
]);

/** How many consecutive words must coincide before a span is marked.
 * Four is the smallest window that reliably skips incidental grammar
 * ("the use of the") while still catching real domain phrases
 * ("the question of interest", "the context of use"). */
export const NGRAM_SIZE = 4;

/** Lowercase + strip everything that isn't a letter, digit or space, so
 * punctuation and markdown table pipes can't break an otherwise identical
 * phrase. */
function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/** Every n-word sequence in `text`, for membership testing. All-stopword
 * sequences are omitted so they can never anchor a highlight. */
export function buildNgrams(text: string, n: number = NGRAM_SIZE): Set<string> {
  const w = words(text);
  const out = new Set<string>();
  for (let i = 0; i + n <= w.length; i++) {
    const gram = w.slice(i, i + n);
    if (gram.every((g) => STOPWORDS.has(g))) continue;
    out.add(gram.join(" "));
  }
  return out;
}

/** Character ranges of `text` that coincide with some n-gram of the answer.
 *
 * Returns half-open `[start, end)` offsets into the ORIGINAL string —
 * punctuation and casing intact — merged so that consecutive matching
 * n-grams become one span rather than a stutter of overlapping marks.
 */
export function matchedSpans(
  text: string,
  answerNgrams: Set<string>,
  n: number = NGRAM_SIZE,
): Array<[number, number]> {
  if (!text || answerNgrams.size === 0) return [];

  // Tokenise while keeping each word's offset in the original string, so a
  // match can be mapped back through the punctuation we stripped.
  const toks: Array<{ w: string; start: number; end: number }> = [];
  const re = /[\p{L}\p{N}]+/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    toks.push({ w: m[0].toLowerCase(), start: m.index, end: m.index + m[0].length });
  }

  const spans: Array<[number, number]> = [];
  for (let i = 0; i + n <= toks.length; i++) {
    const gram = toks.slice(i, i + n);
    if (!answerNgrams.has(gram.map((t) => t.w).join(" "))) continue;
    const start = gram[0].start;
    const end = gram[gram.length - 1].end;
    const last = spans[spans.length - 1];
    // Merge when the new span overlaps or merely abuts the previous one —
    // sliding n-grams over a matching sentence produce a span per word.
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      spans.push([start, end]);
    }
  }
  return spans;
}

/** Wrap every matched span of every text node under `root` in `<mark>`.
 *
 * Operates on the DOM AFTER sanitisation rather than on the markdown or
 * HTML source, so a highlight can never land inside a tag and can never
 * introduce markup — the only nodes touched are text nodes, and the only
 * element created is `<mark>`.
 */
export function highlightAnswerOverlap(root: HTMLElement, answerText: string): void {
  const ngrams = buildNgrams(answerText);
  if (ngrams.size === 0) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) targets.push(node as Text);

  for (const textNode of targets) {
    const text = textNode.nodeValue ?? "";
    const spans = matchedSpans(text, ngrams);
    if (spans.length === 0) continue;

    const frag = document.createDocumentFragment();
    let cursor = 0;
    for (const [start, end] of spans) {
      if (start > cursor) {
        frag.appendChild(document.createTextNode(text.slice(cursor, start)));
      }
      const mark = document.createElement("mark");
      mark.className = "cite-match";
      mark.textContent = text.slice(start, end);
      frag.appendChild(mark);
      cursor = end;
    }
    if (cursor < text.length) {
      frag.appendChild(document.createTextNode(text.slice(cursor)));
    }
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}
